import {
  useForm,
  DefaultValues,
  UseFormReturn,
  FieldValues,
  ResolverResult,
  ResolverOptions,
  FieldErrors,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

type HookProps<T extends z.ZodSchema> = {
  schema: T
  defaultValues?: DefaultValues<z.infer<T>>
  mode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched' | 'all'
  reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit'
  showRequiredErrors?: boolean
}

/**
 * TODO: Remove this hint after testing
 * Custom form hook that extends react-hook-form with smart validation behavior.
 *
 * Key features:
 * - Validates on change (mode: 'onChange')
 * - Can optionally hide "required" field errors when fields are empty (showRequiredErrors=false)
 * - Form will still be invalid (submit disabled) when required fields are empty
 * - Only shows validation errors for fields with invalid content (not just empty)
 *
 * Example: If a user types in a required field then deletes the content,
 * no error message will show (when showRequiredErrors=false). The submit button
 * stays disabled because the form is invalid
 */
const useCommonForm = <T extends z.ZodSchema>({
  schema,
  defaultValues,
  mode = 'onChange',
  reValidateMode = 'onChange',
  showRequiredErrors = true,
}: HookProps<T>): UseFormReturn<z.infer<T>> => {
  const customResolver = async (
    values: FieldValues,
    context: undefined,
    options: ResolverOptions<FieldValues>,
  ): Promise<ResolverResult<FieldValues>> => {
    const result = await zodResolver(schema)(values, context, options)

    if (!result.errors || Object.keys(result.errors).length === 0) {
      return result
    }

    const filteredErrors: FieldErrors<FieldValues> = {}

    Object.keys(result.errors).forEach((fieldName) => {
      const errors = result.errors as FieldErrors<FieldValues>
      const error = errors[fieldName]
      const fieldValue = values[fieldName] as unknown

      const isPassword = fieldName === 'password'

      const isEmpty =
        fieldValue === null || fieldValue === undefined || fieldValue === ''

      const isRequiredError =
        error?.type === 'invalid_type' ||
        error?.type === 'too_small' ||
        (isPassword && error?.type === 'custom')

      if (!(isEmpty && isRequiredError)) {
        filteredErrors[fieldName] = error
      }
    })

    return {
      values: result.values,
      errors: filteredErrors,
    }
  }

  const form = useForm<z.infer<T>>({
    mode,
    reValidateMode,
    resolver: showRequiredErrors ? zodResolver(schema) : customResolver,
    defaultValues,
  })

  return form
}

export default useCommonForm
